import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import useLiveSession from "../live/useLiveSession";
import LiveMirror from "../components/live/LiveMirror";

const JOIN_ERRORS = {
  name_required: "اكتب اسمك أولاً",
  invalid_team: "اختر فريقاً",
  session_not_found: "انتهى البث أو الرابط غير صحيح",
  server_error: "حدثت مشكلة، حاول مرة أخرى",
};

const Shell = ({ children }) => (
  <div
    className="min-h-dvh w-full flex items-center justify-center bg-gradient-to-br from-background via-behindButton to-background p-4"
    style={{ direction: "rtl" }}
  >
    {children}
  </div>
);

const EndCard = ({ title, message }) => (
  <Shell>
    <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl p-8 w-full max-w-md text-center flex flex-col gap-4">
      <h1 className="text-2xl font-bold text-header">{title}</h1>
      <p className="text-dark">{message}</p>
      <Link
        to="/"
        className="bg-mainButton hover:bg-mainButtonShadow text-white font-bold rounded-xl px-6 py-3 transition"
      >
        اكتشف تربع
      </Link>
    </div>
  </Shell>
);

// /live/:id — live spectator page: join with a name + team (locked after
// join), then watch the GM's game mirrored in realtime and vote.
const Live = () => {
  const { id } = useParams();
  const code = (id || "").toUpperCase();
  const session = useLiveSession(code);
  const { phase, meta, joining, joinError, join } = session;

  const [name, setName] = useState("");
  const [teamId, setTeamId] = useState(null);

  if (phase === "notFound") {
    return (
      <EndCard
        title="البث غير موجود"
        message="انتهى البث أو الرابط غير صحيح."
      />
    );
  }
  if (phase === "ended") {
    return <EndCard title="انتهى البث" message="شكراً لمتابعتك! جرب لعب تربع بنفسك." />;
  }

  if (phase === "connecting" || (phase === "join" && !meta)) {
    return (
      <Shell>
        <FaSpinner className="animate-spin text-mainText size-10" />
      </Shell>
    );
  }

  if (phase === "join") {
    return (
      <Shell>
        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-xl p-8 w-full max-w-md flex flex-col gap-5">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-header">
              {meta.gameName || "بث مباشر"}
            </h1>
            <p className="text-subText mt-1">
              انضم للبث، شجّع فريقك وصوّت على القرارات
            </p>
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-bold text-header">اسمك</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={30}
              placeholder="اكتب اسمك"
              className="rounded-xl border-2 border-behindButton/40 focus:border-secondaryButton outline-none px-4 py-3"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="font-bold text-header">اختر فريقك</label>
            <p className="text-subText text-xs">
              لا يمكن تغيير الفريق بعد الانضمام
            </p>
            <div className="grid grid-cols-2 gap-2">
              {(meta.teams || []).map((team) => (
                <button
                  key={team.id}
                  onClick={() => setTeamId(team.id)}
                  className={`rounded-xl px-4 py-3 font-bold transition text-white flex items-center justify-center gap-2
                    ${
                      String(teamId) === String(team.id)
                        ? "bg-secondaryButtonShadow ring-2 ring-mainText"
                        : "bg-secondaryButton hover:bg-secondaryButtonShadow"
                    }`}
                >
                  {team.image && (
                    <span className="w-7 h-7 rounded-full overflow-hidden shrink-0">
                      <img
                        src={team.image}
                        alt={team.name}
                        className="w-full h-full object-cover"
                      />
                    </span>
                  )}
                  {team.name}
                </button>
              ))}
            </div>
          </div>

          {joinError && (
            <p className="text-danger text-center font-bold">
              {JOIN_ERRORS[joinError] || JOIN_ERRORS.server_error}
            </p>
          )}

          <button
            onClick={() => join(name.trim(), teamId)}
            disabled={joining || !name.trim() || teamId == null}
            className="bg-mainButton hover:bg-mainButtonShadow disabled:opacity-50 text-white font-bold rounded-xl px-6 py-3 transition flex items-center justify-center gap-2"
          >
            {joining && <FaSpinner className="animate-spin" />}
            انضم للبث
          </button>
        </div>
      </Shell>
    );
  }

  // live | paused
  return <LiveMirror session={session} />;
};

export default Live;
